import { fetchByIds, fetchOrgMembersWith } from "@/lib/large-org";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { createInvite } from "@/lib/invites.functions";
import {
  ArrowLeft,
  MapPin,
  Users,
  Phone,
  Mail,
  ClipboardList,
  Pencil,
  Loader2,
  Building2,
  CheckCircle2,
  AlertCircle,
  Plus,
  Trash2,
  X,
  Ruler,
  UserPlus,
  Copy,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { PatrolMap } from "@/components/PatrolMap";

export const Route = createFileRoute("/_authenticated/sites/$siteId/")({
  component: SiteDetailPage,
  ssr: false,
  head: () => ({
    meta: [
      { title: "Site detail — MissionReady" },
      { name: "description", content: "Manage geofence, post orders, contacts, staff and client access for this site." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Site = {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  active: boolean;
  latitude: number | null;
  longitude: number | null;
};

type Geofence = {
  id: string;
  name: string;
  kind: string;
  center_lat: number | null;
  center_lng: number | null;
  radius_m: number | null;
  active: boolean;
};

type Member = { user_id: string; role: string | null; full_name: string | null; email: string | null };
type Contact = { id: string; name: string; role_title: string | null; phone: string | null; email: string | null; category: string };
type PostOrder = { id: string; title: string; body: string | null; active: boolean };
type ClientInvite = { id: string; email: string; token: string; accepted_at: string | null; created_at: string };

const CONTACT_CATEGORIES = ["site", "client", "emergency", "vendor"] as const;
const STAFF_ROLES = ["officer", "supervisor", "site_supervisor", "dispatcher", "admin"];

function SiteDetailPage() {
  const { siteId } = useParams({ from: "/_authenticated/sites/$siteId" });
  const { currentOrg, currentRole } = useAuth();
  const navigate = useNavigate();
  const canEdit = ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");
  const canInviteClients = currentRole === "org_owner" || currentRole === "admin";
  const createInviteFn = useServerFn(createInvite);

  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState<Site | null>(null);
  const [geofence, setGeofence] = useState<Geofence | null>(null);
  const [assigned, setAssigned] = useState<Member[]>([]);
  const [orgMembers, setOrgMembers] = useState<Member[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [postOrders, setPostOrders] = useState<PostOrder[]>([]);
  const [clientInvites, setClientInvites] = useState<ClientInvite[]>([]);
  const [busy, setBusy] = useState(false);

  // Post order form
  const [poEditing, setPoEditing] = useState<string | null>(null);
  const [poOpen, setPoOpen] = useState(false);
  const [poTitle, setPoTitle] = useState("");
  const [poBody, setPoBody] = useState("");

  // Contact form
  const [cOpen, setCOpen] = useState(false);
  const [cEditing, setCEditing] = useState<string | null>(null);
  const [cName, setCName] = useState("");
  const [cRole, setCRole] = useState("");
  const [cPhone, setCPhone] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cCategory, setCCategory] = useState<string>("site");

  // Staff / client
  const [pickUser, setPickUser] = useState<string>("");
  const [inviteEmail, setInviteEmail] = useState("");

  const load = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const [siteRes, gfRes, asgRes, contactsRes, ordersRes, membersRes, invitesRes] = await Promise.all([
      supabase.from("sites").select("id,name,address,city,active,latitude,longitude").eq("id", siteId).maybeSingle(),
      supabase.from("geofences").select("id,name,kind,center_lat,center_lng,radius_m,active")
        .eq("site_id", siteId).eq("kind", "inclusion").order("created_at", { ascending: true }).limit(1).maybeSingle(),
      supabase.from("user_site_assignments").select("user_id").eq("site_id", siteId).eq("organization_id", currentOrg.id),
      supabase.from("org_contacts").select("id,name,role_title,phone,email,category")
        .eq("organization_id", currentOrg.id).eq("site_id", siteId).order("name"),
      supabase.from("post_orders").select("id,title,body,active")
        .eq("organization_id", currentOrg.id).eq("site_id", siteId).order("title"),
      // Names come from a second read: there is no PostgREST relationship between
      // organization_members and profiles, so an embed would fail the whole query.
      fetchOrgMembersWith(supabase, currentOrg.id, "user_id,role").then((d) => ({ data: d })),
      supabase.from("organization_invites")
        .select("id,email,token,accepted_at,created_at,site_ids")
        .eq("organization_id", currentOrg.id).eq("role", "client_viewer")
        .order("created_at", { ascending: false }),
    ]);

    if (!siteRes.data) {
      toast.error("Site not found");
      navigate({ to: "/sites" });
      return;
    }
    setSite(siteRes.data as Site);
    setGeofence((gfRes.data as Geofence | null) ?? null);
    setContacts((contactsRes.data ?? []) as Contact[]);
    setPostOrders((ordersRes.data ?? []) as PostOrder[]);

    const memberRows = (membersRes.data ?? []) as any[];
    const memberIds = memberRows.map((m) => m.user_id);
    const profRes = memberIds.length
      ? await fetchByIds<any>("profiles", "id,full_name,email", memberIds, "id", supabase).then((d) => ({ data: d, error: null }))
      : { data: [] as any[] };
    const profById = new Map((profRes.data ?? []).map((p: any) => [p.id, p]));
    const members: Member[] = memberRows.map((m) => ({
      user_id: m.user_id,
      role: m.role,
      full_name: profById.get(m.user_id)?.full_name ?? null,
      email: profById.get(m.user_id)?.email ?? null,
    }));
    setOrgMembers(members.filter((m) => STAFF_ROLES.includes(m.role ?? "") || m.role === "org_owner"));

    const assignedIds = new Set((asgRes.data ?? []).map((r: any) => r.user_id));
    setAssigned(members.filter((m) => assignedIds.has(m.user_id)));

    setClientInvites(
      ((invitesRes.data ?? []) as any[])
        .filter((i) => !Array.isArray(i.site_ids) || i.site_ids.length === 0 || i.site_ids.includes(siteId))
        .map((i) => ({ id: i.id, email: i.email, token: i.token, accepted_at: i.accepted_at, created_at: i.created_at })),
    );
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentOrg?.id, siteId]);

  /* ---------------- Post orders ---------------- */
  const openNewPo = () => { setPoEditing(null); setPoTitle(""); setPoBody(""); setPoOpen(true); };
  const openEditPo = (p: PostOrder) => { setPoEditing(p.id); setPoTitle(p.title); setPoBody(p.body ?? ""); setPoOpen(true); };

  const savePo = async () => {
    if (!currentOrg) return;
    if (!poTitle.trim()) return toast.error("Give the post order a title");
    setBusy(true);
    const payload = { title: poTitle.trim(), body: poBody.trim() || null };
    const { error } = poEditing
      ? await supabase.from("post_orders").update(payload).eq("id", poEditing)
      : await supabase.from("post_orders").insert({
          ...payload, organization_id: currentOrg.id, site_id: siteId, active: true,
        });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(poEditing ? "Post order updated" : "Post order added");
    setPoOpen(false); setPoEditing(null); setPoTitle(""); setPoBody("");
    load();
  };

  const togglePo = async (p: PostOrder) => {
    const { error } = await supabase.from("post_orders").update({ active: !p.active }).eq("id", p.id);
    if (error) return toast.error(error.message);
    load();
  };

  const deletePo = async (p: PostOrder) => {
    if (!confirm(`Delete post order "${p.title}"?`)) return;
    const { error } = await supabase.from("post_orders").delete().eq("id", p.id);
    if (error) return toast.error(error.message);
    toast.success("Post order deleted");
    load();
  };

  /* ---------------- Contacts ---------------- */
  const openNewContact = () => {
    setCEditing(null); setCName(""); setCRole(""); setCPhone(""); setCEmail(""); setCCategory("site"); setCOpen(true);
  };
  const openEditContact = (c: Contact) => {
    setCEditing(c.id); setCName(c.name); setCRole(c.role_title ?? ""); setCPhone(c.phone ?? "");
    setCEmail(c.email ?? ""); setCCategory(c.category); setCOpen(true);
  };

  const saveContact = async () => {
    if (!currentOrg) return;
    if (!cName.trim()) return toast.error("Contact name is required");
    setBusy(true);
    const payload = {
      name: cName.trim(),
      role_title: cRole.trim() || null,
      phone: cPhone.trim() || null,
      email: cEmail.trim() || null,
      category: cCategory,
    };
    const { error } = cEditing
      ? await supabase.from("org_contacts").update(payload).eq("id", cEditing)
      : await supabase.from("org_contacts").insert({
          ...payload, organization_id: currentOrg.id, site_id: siteId,
        });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(cEditing ? "Contact updated" : "Contact added");
    setCOpen(false);
    load();
  };

  const deleteContact = async (c: Contact) => {
    if (!confirm(`Remove contact ${c.name}?`)) return;
    const { error } = await supabase.from("org_contacts").delete().eq("id", c.id);
    if (error) return toast.error(error.message);
    toast.success("Contact removed");
    load();
  };

  /* ---------------- Staff ---------------- */
  const assignUser = async () => {
    if (!currentOrg || !pickUser) return toast.error("Pick someone to assign");
    setBusy(true);
    const { error } = await supabase.from("user_site_assignments").insert({
      organization_id: currentOrg.id, user_id: pickUser, site_id: siteId,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Assigned to site");
    setPickUser("");
    load();
  };

  const unassignUser = async (userId: string) => {
    if (!currentOrg) return;
    const { error } = await supabase.from("user_site_assignments").delete()
      .eq("organization_id", currentOrg.id).eq("site_id", siteId).eq("user_id", userId);
    if (error) return toast.error(error.message);
    toast.success("Removed from site");
    load();
  };

  /* ---------------- Client invite ---------------- */
  const sendClientInvite = async () => {
    if (!currentOrg) return;
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return toast.error("Enter the client's email");
    setBusy(true);
    try {
      const res: any = await createInviteFn({
        data: {
          orgId: currentOrg.id,
          email,
          role: "client_viewer",
          siteIds: [siteId],
          origin: typeof window !== "undefined" ? window.location.origin : undefined,
        },
      });
      const link = `${window.location.origin}/accept-invite/${res.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      if (res?.emailSent) toast.success(`Client invite emailed to ${email} — link also copied`);
      else toast.warning(`Client invite created, but the email couldn't be sent${res?.emailError ? ` (${res.emailError})` : ""}. Link copied — share it directly.`);

      setInviteEmail("");
      load();
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to create invite");
    } finally {
      setBusy(false);
    }
  };

  const copyInvite = async (token: string) => {
    await navigator.clipboard.writeText(`${window.location.origin}/accept-invite/${token}`);
    toast.success("Invite link copied");
  };

  if (loading || !site) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading site…
      </div>
    );
  }

  const mapMarkers =
    geofence && geofence.center_lat != null && geofence.center_lng != null
      ? [{
          id: geofence.id,
          name: geofence.name,
          kind: "inclusion" as const,
          center_lat: Number(geofence.center_lat),
          center_lng: Number(geofence.center_lng),
          radius_m: Number(geofence.radius_m ?? 150),
        }]
      : site.latitude != null && site.longitude != null
      ? [{
          id: site.id, name: site.name, kind: "inclusion" as const,
          center_lat: Number(site.latitude), center_lng: Number(site.longitude), radius_m: 60,
        }]
      : [];

  const unassigned = orgMembers.filter((m) => !assigned.some((a) => a.user_id === m.user_id));
  const label = (m: Member) => m.full_name ?? m.email ?? "Unknown member";

  return (
    <div className="min-h-dvh bg-muted/40">
      <header className="sticky top-0 z-10 bg-foreground text-background">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-4">
          <Link to="/sites" className="rounded-md p-1 hover:bg-background/10" aria-label="Back to sites">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <Building2 className="h-5 w-5 shrink-0" />
          <h1 className="font-display truncate text-xl font-bold">{site.name}</h1>
        </div>
      </header>

      <div className="mx-auto max-w-4xl space-y-4 px-4 py-5">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" asChild>
            <Link to="/sites/$siteId/checkpoints" params={{ siteId }}>
              <ClipboardList className="mr-1 h-4 w-4" /> On-site checkpoints
            </Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/site-activity">
              <MapPin className="mr-1 h-4 w-4" /> Site activity
            </Link>
          </Button>
        </div>

        {/* Core info */}
        <section className="rounded-2xl border border-border bg-card p-4">
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <div className="font-medium">{site.address ?? "No address on file"}</div>
                {site.city && <div className="text-xs text-muted-foreground">{site.city}</div>}
                {site.latitude != null && site.longitude != null && (
                  <div className="text-xs text-muted-foreground">
                    {Number(site.latitude).toFixed(5)}, {Number(site.longitude).toFixed(5)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {geofence ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2.5 py-1 text-xs font-semibold text-success">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Perimeter set
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-warning/15 px-2.5 py-1 text-xs font-semibold text-warning">
                  <AlertCircle className="h-3.5 w-3.5" /> No geofence yet
                </span>
              )}
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                <Ruler className="h-3.5 w-3.5" /> {geofence?.radius_m ? `${Math.round(Number(geofence.radius_m))} m radius` : "Radius not set"}
              </span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${site.active ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
                {site.active ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </section>

        <Tabs defaultValue="geofence">
          <TabsList className="w-full overflow-x-auto">
            <TabsTrigger value="geofence">Geofence</TabsTrigger>
            <TabsTrigger value="orders">Post orders</TabsTrigger>
            <TabsTrigger value="contacts">Contacts</TabsTrigger>
            <TabsTrigger value="staff">Guards</TabsTrigger>
            <TabsTrigger value="client">Client</TabsTrigger>
          </TabsList>

          {/* ---- Geofence ---- */}
          <TabsContent value="geofence" className="mt-3 space-y-3">
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">Geofence &amp; perimeter</h2>
                {canEdit && (
                  <Button size="sm" asChild>
                    <Link to="/sites/$siteId/edit" params={{ siteId }}>
                      <Pencil className="mr-1 h-4 w-4" /> {geofence ? "Adjust perimeter" : "Set up geofence"}
                    </Link>
                  </Button>
                )}
              </div>
              {mapMarkers.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-border">
                  <PatrolMap geofences={mapMarkers} height={300} fitTo="geofences" defaultStyle="hybrid" enableStyleToggle />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No coordinates yet — set up the geofence to place this site on the map.
                </p>
              )}
              <p className="mt-3 text-xs text-muted-foreground">
                The perimeter drives clock-in validation, geofence entry/exit events and patrol tracking for this site.
              </p>
            </section>
          </TabsContent>

          {/* ---- Post orders ---- */}
          <TabsContent value="orders" className="mt-3 space-y-3">
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <ClipboardList className="h-4 w-4" /> Post orders ({postOrders.length})
                </h2>
                {canEdit && (
                  <Button size="sm" onClick={openNewPo}><Plus className="mr-1 h-4 w-4" /> Add</Button>
                )}
              </div>

              {poOpen && (
                <div className="mb-4 space-y-3 rounded-xl border border-border bg-muted/40 p-3">
                  <div>
                    <Label htmlFor="po-title">Title</Label>
                    <Input id="po-title" value={poTitle} onChange={(e) => setPoTitle(e.target.value)} placeholder="Lobby access control" />
                  </div>
                  <div>
                    <Label htmlFor="po-body">Instructions</Label>
                    <Textarea id="po-body" rows={5} value={poBody} onChange={(e) => setPoBody(e.target.value)}
                      placeholder="Step-by-step duties, escalation contacts, restricted areas…" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={savePo} disabled={busy}>
                      <Save className="mr-1 h-4 w-4" /> {poEditing ? "Save changes" : "Add post order"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setPoOpen(false); setPoEditing(null); }}>
                      <X className="mr-1 h-4 w-4" /> Cancel
                    </Button>
                  </div>
                </div>
              )}

              {postOrders.length === 0 ? (
                <p className="text-sm text-muted-foreground">No post orders for this site yet.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {postOrders.map((p) => (
                    <li key={p.id} className="py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{p.title}</span>
                            {!p.active && <span className="rounded-full bg-muted px-2 py-0.5 text-xs">Inactive</span>}
                          </div>
                          {p.body && <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{p.body}</p>}
                        </div>
                        {canEdit && (
                          <div className="flex shrink-0 gap-1">
                            <Button size="icon" variant="ghost" onClick={() => openEditPo(p)} aria-label="Edit post order">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => togglePo(p)}>
                              {p.active ? "Disable" : "Enable"}
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => deletePo(p)} aria-label="Delete post order">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </TabsContent>

          {/* ---- Contacts ---- */}
          <TabsContent value="contacts" className="mt-3 space-y-3">
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Phone className="h-4 w-4" /> Points of contact ({contacts.length})
                </h2>
                {canEdit && (
                  <Button size="sm" onClick={openNewContact}><Plus className="mr-1 h-4 w-4" /> Add</Button>
                )}
              </div>

              {cOpen && (
                <div className="mb-4 space-y-3 rounded-xl border border-border bg-muted/40 p-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="c-name">Name</Label>
                      <Input id="c-name" value={cName} onChange={(e) => setCName(e.target.value)} placeholder="Jane Doe" />
                    </div>
                    <div>
                      <Label htmlFor="c-role">Role / title</Label>
                      <Input id="c-role" value={cRole} onChange={(e) => setCRole(e.target.value)} placeholder="Property manager" />
                    </div>
                    <div>
                      <Label htmlFor="c-phone">Phone</Label>
                      <Input id="c-phone" type="tel" value={cPhone} onChange={(e) => setCPhone(e.target.value)} placeholder="(555) 010-1234" />
                    </div>
                    <div>
                      <Label htmlFor="c-email">Email</Label>
                      <Input id="c-email" type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} placeholder="jane@client.com" />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={cCategory} onValueChange={setCCategory}>
                        <SelectTrigger aria-label="Category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CONTACT_CATEGORIES.map((k) => (
                            <SelectItem key={k} value={k}>{k}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveContact} disabled={busy}>
                      <Save className="mr-1 h-4 w-4" /> {cEditing ? "Save changes" : "Add contact"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setCOpen(false); setCEditing(null); }}>
                      <X className="mr-1 h-4 w-4" /> Cancel
                    </Button>
                  </div>
                </div>
              )}

              {contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No contacts on file for this site.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {contacts.map((c) => (
                    <li key={c.id} className="py-3 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium">
                            {c.name}
                            {c.role_title && <span className="text-muted-foreground"> — {c.role_title}</span>}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                            {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-foreground"><Phone className="h-3 w-3" />{c.phone}</a>}
                            {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-foreground"><Mail className="h-3 w-3" />{c.email}</a>}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{c.category}</span>
                          {canEdit && (
                            <>
                              <Button size="icon" variant="ghost" onClick={() => openEditContact(c)} aria-label="Edit contact">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => deleteContact(c)} aria-label="Delete contact">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </TabsContent>

          {/* ---- Staff ---- */}
          <TabsContent value="staff" className="mt-3 space-y-3">
            <section className="rounded-2xl border border-border bg-card p-4">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Users className="h-4 w-4" /> Guards &amp; staff ({assigned.length})
              </h2>

              {canEdit && (
                <div className="mb-4 flex flex-col gap-2 rounded-xl border border-border bg-muted/40 p-3 sm:flex-row">
                  <Select value={pickUser} onValueChange={setPickUser}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={unassigned.length ? "Select a team member…" : "Everyone is already assigned"} />
                    </SelectTrigger>
                    <SelectContent>
                      {unassigned.map((m) => (
                        <SelectItem key={m.user_id} value={m.user_id}>
                          {label(m)}{m.role ? ` · ${m.role}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={assignUser} disabled={busy || !pickUser}>
                    <UserPlus className="mr-1 h-4 w-4" /> Assign
                  </Button>
                </div>
              )}

              {assigned.length === 0 ? (
                <p className="text-sm text-muted-foreground">No guards or staff assigned to this site yet.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {assigned.map((m) => (
                    <li key={m.user_id} className="flex items-center justify-between gap-2 py-2 text-sm">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{label(m)}</div>
                        <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {m.role && <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{m.role}</span>}
                        {canEdit && (
                          <Button size="sm" variant="ghost" onClick={() => unassignUser(m.user_id)}>Remove</Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {canEdit && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Need to add someone new to the company first? <Link to="/team" className="underline">Invite them to your team</Link>.
                </p>
              )}
            </section>
          </TabsContent>

          {/* ---- Client ---- */}
          <TabsContent value="client" className="mt-3 space-y-3">
            <section className="rounded-2xl border border-border bg-card p-4">
              <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold">
                <UserPlus className="h-4 w-4" /> Invite a client
              </h2>
              <p className="mb-3 text-xs text-muted-foreground">
                Client contacts get read-only portal access scoped to this site only.
              </p>

              {canInviteClients ? (
                <div className="flex flex-col gap-2 rounded-xl border border-border bg-muted/40 p-3 sm:flex-row">
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="client@company.com"
                    className="flex-1"
                  />
                  <Button onClick={sendClientInvite} disabled={busy || !inviteEmail.trim()}>
                    <Mail className="mr-1 h-4 w-4" /> Send invite
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Only an owner or admin can invite clients.</p>
              )}

              <div className="mt-4">
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Client invites for this site ({clientInvites.length})
                </h3>
                {clientInvites.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No client invites yet.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {clientInvites.map((i) => (
                      <li key={i.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                        <div className="min-w-0">
                          <div className="truncate font-medium">{i.email}</div>
                          <div className="text-xs text-muted-foreground">
                            {i.accepted_at ? "Accepted" : "Pending"} · {new Date(i.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {!i.accepted_at && (
                          <Button size="sm" variant="ghost" onClick={() => copyInvite(i.token)}>
                            <Copy className="mr-1 h-4 w-4" /> Copy link
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Manage all client access from <Link to="/clients" className="underline">Client Access</Link>.
              </p>
            </section>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
